// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, flatMap, map, tap } from 'rxjs/operators';
import * as urltemplate from 'url-template';
import { trim } from 'lodash-es';
import { AppConstants } from '@utils/app.constants';
import { LMActionService, LMActionRequest } from '@services/lm-action.service';
import {
  ConfirmationDialogResult,
  LMConfirmationMessageService
} from '@modal/lm-confirmation-message.service';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { LMLoggerService } from '@services/lm-logger.service';
import { LMFieldUtilService } from '@services/utils/lm-field-util.service';
import { LMSpecService } from '@services/lm-spec.service';
import {
  LMResponseRequiredService,
  ResponseRequiredDialogResult,
  ResponseRequiredResult
} from '@modal/lm-response-required.service';
import {
  LMActionFormDialogService,
  LMActionFormDialogResult,
  LMActionFormDialogResultCode
} from '@modal/lm-action-form-dialog.service';
import { LMFormContext } from '@form/services/lm-form-action-util.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { LMScheduleDialogService } from '@modal/print-dialog/schedule/lm-schedule-dialog.service';
import { LMUrlUtilService } from '@services/utils/lm-url-util.service';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMContextService } from '@services/lm-context.service';
import { LMUrlInfoBuilderService } from '@services/lm-url-info-builder.service';
import { LMMultiPartFields, LMTransactionState } from './lm-transaction-state';

@Injectable()
export class LMTransactionService {
  private turnOnOKToAllResponseRequired = false;

  constructor(
    private lmActionFormDialogService: LMActionFormDialogService,
    private lmActionService: LMActionService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmConfirmationMessageService: LMConfirmationMessageService,
    private lmContextService: LMContextService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmFieldUtilService: LMFieldUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmLoggerService: LMLoggerService,
    private lmResponseRequiredService: LMResponseRequiredService,
    private lmScheduleDialogService: LMScheduleDialogService,
    private lmSpecService: LMSpecService,
    private lmUrlInfoBuilderService: LMUrlInfoBuilderService,
    private lmUrlUtilService: LMUrlUtilService
  ) {}

  /**
   * Determines if opening a schedule dialog is necessary or not
   * then returns a schedule dataview accordingly
   * @param           actionSpec   the action spec
   * @param                 busClassName the busclass name
   * @return              the schedule dataview
   */
  public getScheduleView(actionSpec: LMActionSpec, busClassName: string): Observable<LMDataView> {
    // Open schedule dialog if it is not an action form and action needs to run in background
    const hasFormDialog = actionSpec.hasParameters || actionSpec.hasActionForm;
    return (!hasFormDialog && actionSpec.willRunAsynchronous) ?
      this.lmScheduleDialogService.openScheduleDialog(busClassName, actionSpec.name) :
      of(<LMDataView>{});
  }

  /**
   * Helper function intended to be used with rxjs' tap() operator that will
   * throw a custom error to signify that previous transaction is cancelled
   * therefore skipping all operator functions that follows
   * @param proceed the boolean return value it's going to receive
   */
  public isCancelled(proceed: boolean) {
    if (!proceed) {
      throw 'cancelled';
    }
  }

  /**
   * Helper function intended to be used with rxjs' catchError() operator that will
   * catch cancelled errors thrown by isCancelled and exit with a false value instead
   * @param                 error any error that may occur within the observable stream
   * @return       returns false if 'cancelled' error is caught
   */
  public checkCancelled(error: any): Observable<boolean> {
    if (error === 'cancelled') {
      return of(false);
    }
    // let other kinds of error fall through
    throw error;
  }

  public runCheckConfirmationRequired(actionSpec: LMActionSpec, state: LMTransactionState): Observable<boolean> {
    const required = actionSpec && (actionSpec.confirmation || actionSpec.type === AppConstants.ACTION_TYPE_DELETE);
    if (!required) {
      return of(true);
    }
    const dataView = state.transactionRequestList ? state.transactionRequestList[0].dataView : null;

    // confirmation message
    const message = (actionSpec.confirmation) ?
      this.lmFormatUtilService.formatMessageRule(actionSpec.confirmation, dataView) :
      this.lmFormatUtilService.formatMessage(Soho.Locale.translate('ConfirmDelete'), actionSpec.label);

    // alert message appended to title
    let alertMessage = null;
    if (state.transactionRequestList && state.transactionRequestList.length > 1) {
      const count = state.transactionRequestList.length;
      const phrase = (actionSpec.type === AppConstants.ACTION_TYPE_DELETE) ?
        'DeleteConfirmationAlertMessage' :
        'ActionConfirmationAlertMessage';
      alertMessage = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(phrase), count);
    }

    // TODO need to add the confirmation alert message if more that one dataview is selected!
    return this.lmConfirmationMessageService.promptConfirmationMessage({ message, alertMessage }).pipe(
      map((result: ConfirmationDialogResult) => {
        if (result === ConfirmationDialogResult.CONFIRMATION_OK) {
          this.lmLoggerService.log([ 'Message confirmed: ', message ]);
          return true;
        }
        this.lmLoggerService.log([ 'Message not confirmed: ', message ]);
        state.cancel();
        return false;
      })
    );
  }

  public runCheckFormInputRequired(
    actionRedirectURL: string,
    actionSpec: LMActionSpec,
    transactionState: LMTransactionState,
    stepUpActionResult?: LMActionResult,
    contextDataView?: LMDataView,
    actionFieldMap?: LMActionFieldMap,
    dragdropTargetView?: LMDataView
  ): Observable<boolean> {
    const required = actionSpec && (actionSpec.hasParameters || actionSpec.hasActionForm);
    if (!required) {
      return of(true);
    }

    this.lmLoggerService.time({
      id: 'TotalActionForm',
      description: 'TOTAL ACTION FORM',
      className: 'LMTransactionService',
      timingStart: 'LMTransactionService -> runCheckFormInputRequired()',
      timingEnd: 'LMFormContentComponent -> ngAfterViewInit()'
    });

    return this.lmSpecService.getFormSpecFromRedirectActionUrl(actionRedirectURL, stepUpActionResult).pipe(
      flatMap((response: LMActionFormUrlResponse | LMSystemException) => {

        if ((response as LMSystemException).status
          && ((response as LMSystemException).status === AppConstants.STATUS_SYSTEM_ERROR
            || (response as LMSystemException).status === AppConstants.STATUS_UNAUTHORIZED)) {
          const paramView = {} as LMDataView;
          return of({ response, paramView });
        }

        const actionFormUrlResponse = response as LMActionFormUrlResponse;
        const formUrlInfo = actionFormUrlResponse.formUrlInfo;
        const resourceId = actionFormUrlResponse.resourceId;
        const actionName = actionFormUrlResponse.action;
        const urlCompiledTemplate = urltemplate.parse(formUrlInfo.initialDataUrl);
        const url = urlCompiledTemplate.expand({ resourceId, actionName });

        return this.lmActionFormDialogService.getActionFormDialogDataView(url).pipe(
          map((paramView: LMDataView) => ({ response, paramView }))
        );
      }),
      flatMap(({ response, paramView }) => {
        // Exception Occurs
        const exception: LMSystemException =  (Object.keys(paramView).length === 0)
          ? (response as LMSystemException)
          : (paramView.status !== AppConstants.STATUS_COMPLETED) ? (paramView as LMSystemException) : undefined;

        if (exception) {
          this.lmLoggerService.error(exception);
          // oomponents and not the service will handle the errors. Send the error back in the transaction state.
          // this.lmErrorUtilService.handleError(exception.message, exception.details, exception.status, exception.statusText);
          transactionState.error(exception);
          return of(false);
        }

        const actionFormUrlResponse = response as LMActionFormUrlResponse;
        const formUrlInfo = actionFormUrlResponse.formUrlInfo;
        const formSpec = actionFormUrlResponse.formSpec;

        let allParamsSet;
        if (contextDataView) {
          paramView = this.populateParamView(paramView, contextDataView, formSpec, false, actionFieldMap);
          allParamsSet = this.allParamsSet(paramView.fields, formSpec.busFormSpec.fields, actionFieldMap);
        } else if (dragdropTargetView) {
          paramView = this.populateFromDragDropView(paramView, dragdropTargetView, formSpec, false, actionFieldMap);
          allParamsSet = this.allParamsSet(paramView.fields, formSpec.busFormSpec.fields);
        } else if (actionSpec.hasActionForm && !actionSpec.hasParameters) {
          allParamsSet = (
            actionSpec.type !== AppConstants.ACTION_TYPE_CREATE &&
            actionSpec.type !== AppConstants.ACTION_TYPE_IMPORT &&
            actionSpec.type !== AppConstants.ACTION_TYPE_UPDATE &&
            !this.lmResponseRequiredService.isResponseRequired(actionSpec)
          );
        }

        if (!allParamsSet) {
          return this.runActionFormDialog(formUrlInfo, formSpec, actionSpec, paramView, transactionState, true);
        }

        return this.executeRunActionForm({ formSpec, formUrlInfo }, actionSpec, paramView, transactionState);
      })
    );
  }

  private runActionFormDialog(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    actionSpec: LMActionSpec,
    paramView: LMDataView,
    transactionState: LMTransactionState,
    loadTimerStarted?: boolean,
  ): Observable<boolean> {
    return this.lmActionFormDialogService
      .runActionFormDialog(formUrlInfo, formSpec, actionSpec, paramView, <any>transactionState, null, loadTimerStarted)
      .pipe(map((actionFormDialogResult: LMActionFormDialogResult) => {
        const result = actionFormDialogResult.result;
        if (result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_CANCEL) {
          transactionState.cancel();
          return false;
        }

        if (result === LMActionFormDialogResultCode.ACTION_FORM_DIALOG_SAVED) {
          // Transaction state is already updated in form component aftre successful save.
          return false;
        }
        return true;
      }));
  }

  private executeRunActionForm(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    paramView: LMDataView,
    transactionState: LMTransactionState
  ): Observable<boolean> {
    return this.runActionForm(formContext, actionSpec, paramView, transactionState.getPendingDataViews()).pipe(
      map((formTransactionState: LMTransactionState) => {
        if (formTransactionState.isCanceled) {
          transactionState.cancel();
          return false;
        }

        if (formTransactionState.isFinished) {
          const completedList = formTransactionState.completedTransactionList;
          completedList.forEach((completed) => transactionState.completeResult(completed));

          if (formTransactionState.redirectUrlToGuestReg) {
            transactionState.redirectUrlToGuestReg = formTransactionState.redirectUrlToGuestReg;
          }

          const cancelled = formTransactionState.cancelledRTConfirmations;
          if (cancelled.length) {
            transactionState.cancelledRTConfirmations.push(...cancelled);
          }
          transactionState.finish();
          return false;
        }
        return true;
      })
    );
  }

  public runActionForm(
    formContext: LMFormContext,
    actionSpec: LMActionSpec,
    parameterView: LMDataView,
    dataViewList: LMDataView[],
    multiPartFields?: LMMultiPartFields
  ): Observable<LMTransactionState> {
    const formSpec = formContext.formSpec;
    const busClassSpec = formSpec.busClassSpec;
    const busFormSpec = formSpec.busFormSpec;
    const url = this.createFormTransactionUrl(formContext, actionSpec, parameterView);
    const transactionState = new LMTransactionState(url);
    const scheduleView$ = this.getScheduleView(actionSpec, busClassSpec.name);

    const proceed$ = scheduleView$.pipe(
      filter(scheduleView => (scheduleView !== undefined)),
      // Prepare transactionRequests
      map((scheduleDV: LMDataView) => {
        // for multiple record selection
        if (dataViewList.length > 1) {
          // derive scheduleView from parameterView when available
          if (!Object.keys(scheduleDV).length && parameterView.scheduleView) {
            scheduleDV = parameterView.scheduleView;
          }
          transactionState.parameterView = parameterView;
          return this.lmActionService.buildActionRequestForDataViewList({
            dataViewLists: dataViewList,
            actionSpec,
            scheduleView: scheduleDV,
            pfActionRequestMode: formContext.actionRequestMode
          });
        }

        return [this.lmActionService.buildActionRequestForDataView(parameterView, actionSpec, null, null, null, scheduleDV, formContext.actionRequestMode)];
      }),
      // Prepare transactionState
      map((transactionRequests: LMActionRequest[]) => {
        transactionState.setRequestPayload(transactionRequests);
        transactionState.busFormSpec = busFormSpec;
        if (multiPartFields) {
          // Attachment lists - The multipart fields are in the original transaction state and not in model.
          transactionState.multiPartFields = multiPartFields;
        } else if (formContext.formModel) {
          transactionState.multiPartFields = formContext.formModel.multiPartFields;
        }
        return transactionState;
      }),
      flatMap(() => this.runCheckResponseRequired(transactionState, busClassSpec, busFormSpec)),
      tap(this.isCancelled),
      flatMap(() => this.runCheckConfirmationRequired(actionSpec, transactionState)),
      tap(this.isCancelled),
      catchError(this.checkCancelled)
    );

    return proceed$.pipe(
      flatMap((proceed: boolean) => {
        if (proceed) {
          return this.executeAction(transactionState);
        }

        transactionState.cancel();
        return of(transactionState);
      })
    );
  }

  public createFormTransactionUrl(formContext: LMFormContext, actionSpec: LMActionSpec, dataView: LMDataView, sourceResourceId?: string) {
    const newFormTransactionUrl = this.createNewFormTransactionUrl(formContext, actionSpec, dataView, sourceResourceId);

    let formTransactionUrl;
    if (this.lmContextService.useNewSpecUrls()) {
      formTransactionUrl = newFormTransactionUrl;
    } else {
      const oldTransactionUrl = this.createOldFormTransactionUrl(formContext.formUrlInfo, formContext.formSpec, actionSpec, dataView, sourceResourceId);

      this.lmUrlInfoBuilderService.compareSpecUrls(
        formContext.formUrlInfo,
        formContext.formSpec.busFormSpec.name + 'LMTransactionService.createFormTransactionUrl',
        oldTransactionUrl,
        newFormTransactionUrl,
      );
      formTransactionUrl = oldTransactionUrl;
    }

    return formTransactionUrl;
  }

  /**
   * @deprecated spec-url-refactor can remove once refactor is complete.
   */
  private createOldFormTransactionUrl(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, actionSpec: LMActionSpec, dataView: LMDataView, sourceResourceId?: string) {
    // DPB This needs to be tested
    if (formUrlInfo?.sourceResource?.resourceType === AppConstants.RESOURCETYPE_FORMINVOKE && actionSpec.type === 'CREATE') {
      return formUrlInfo.formInvokeCreateUrl;
    }

    const transactionUrlTemplate = urltemplate.parse(formUrlInfo.transactionUrl);
    let url;
    if (formSpec.dependentForm) {
      url = transactionUrlTemplate.expand({
        resourceId: dataView.resourceId,
        actionName: actionSpec.name,
        sourceResourceId
      });
    } else {
      url = transactionUrlTemplate.expand({
        resourceId: dataView.resourceId,
        actionName: actionSpec.name
      });
    }

    if (formUrlInfo.navigationContext && Object.keys(formUrlInfo.navigationContext).length > 0) {
      const delimiter = url.indexOf('?') === -1 ? '?' : '&';
      url += delimiter + this.lmUrlUtilService.objectToQueryString(formUrlInfo.navigationContext);
    }
    return url;
  }

  private createNewFormTransactionUrl(formContext: LMFormContext, actionSpec: LMActionSpec, dataView: LMDataView, sourceResourceId?: string) {
    // DPB This needs to be tested
    if (formContext.formUrlInfo.sourceResource
      &&  formContext.formUrlInfo.sourceResource.resourceType === AppConstants.RESOURCETYPE_FORMINVOKE
      &&  actionSpec.type === 'CREATE') {
      return formContext.formUrlInfo.formInvokeCreateUrl;
    }

    const transactionUrlTemplate = urltemplate.parse(formContext.formUrlInfo.transactionUrl);
    const params = {
      resourceId: dataView.resourceId,
      actionName: actionSpec.name
    };

    if (formContext.formSpec.dependentForm) {
      params['sourceResourceId'] = sourceResourceId;
    }

    let url = transactionUrlTemplate.expand(params);
    if (formContext.formUrlInfo.navigationContext && Object.keys(formContext.formUrlInfo.navigationContext).length > 0) {
      const delimiter = url.indexOf('?') === -1 ? '?' : '&';
      url += delimiter + this.lmUrlUtilService.objectToQueryString(formContext.formUrlInfo.navigationContext);
    }

    return url;
  }

  private populateFromDragDropView(paramView: LMDataView, dragdropTargetView: LMDataView, formSpec: LMFormSpec, override: boolean, actionFieldMap?: LMActionFieldMap): LMDataView {
    /*
     Object.assign is a shallow clone, it is NOT cloning the objects in the field collection.
     I'm leaving this because the Action Form's model ends up updating this field collection which then
     updates all the fields the pending transaction dataViews.  Risk is that if the form model changes
     a non parameter field such as a context key or symbolic key.
     */
    const dvFields = Object.assign({}, paramView.fields);

    const keys: Array<string> = Object.keys(dvFields);

    for (let index = 0, len = keys.length; index < len; index++) {
      const key = keys[ index ];
      this.processParamFieldValue(key, dvFields, dragdropTargetView, formSpec, override, actionFieldMap, true);
    }
    paramView.fields = dvFields;

    this.isValidSection(paramView, dragdropTargetView, formSpec);
    return paramView;
  }

  private populateParamView(paramView: LMDataView, contextDataView: LMDataView, formSpec: LMFormSpec, override: boolean, actionFieldMap?: LMActionFieldMap): LMDataView {
    const dataView = { ...paramView };
    dataView.id = contextDataView.id; // this is necessary so list can update the row

    const keys: Array<string> = Object.keys(dataView.fields);
    for (let index = 0, len = keys.length; index < len; index++) {
      const key = keys[index];
      this.processParamFieldValue(key, dataView.fields, contextDataView, formSpec, override, actionFieldMap, false);
    }

    return dataView;
  }

  private processParamFieldValue(key: string, dvFields: LMFields, contextDataView: LMDataView, formSpec: LMFormSpec, override: boolean, actionFieldMap: LMActionFieldMap, dragAndDrop: boolean) {
    const dvField = dvFields[ key ];
    let isBlank = true;
    let prefix = '';

    if (dvField.members) {
      for (let i = 0, len = dvField.members.length; i < len; i++) {
        const memberId = dvField.members[ i ];
        this.processParamFieldValue(memberId, dvFields, contextDataView, formSpec, override, actionFieldMap, dragAndDrop);
      }
    } else {
      let contextFieldId = key;

      const indexPrd = key.indexOf('_prd_');
      if (indexPrd > -1) {
        const indexOp = key.startsWith('_op_');
        contextFieldId = key.substring(indexPrd + 5);
        if (indexOp) {
          contextFieldId = '_op_' + contextFieldId;
        }
        prefix = key.substring(0, indexPrd);
      }

      let contextField;
      if (actionFieldMap && actionFieldMap[ contextFieldId ]) {
        const fieldMapId = actionFieldMap[ contextFieldId ];
        if (contextDataView.fields[ fieldMapId ]) {
          contextField = contextDataView.fields[ fieldMapId ];
        }
        // LMCLIENT-36804: If drag and drop and part of field mapping, override field.
        // Prior to this change, we had an issue of not being able to override
        // Boolean fields (only) AND a related field of the invoked field.
        if (dragAndDrop) {
          override = true;
        }
      } else if (actionFieldMap && prefix.length > 0 && actionFieldMap[ prefix ]) {
        const fieldMapPrefix = actionFieldMap[ prefix ];
        const fieldMapId =  fieldMapPrefix + '_prd_' + contextFieldId;
        if (contextDataView.fields[ fieldMapId ]) {
          contextField = contextDataView.fields[ fieldMapId ];
        } else if (dragAndDrop && contextDataView.fields[ contextFieldId ]) { // actionFieldMap is required for drag and drop but they aren't necessarily used for prefix mapping
          contextField = contextDataView.fields[contextFieldId];
        }
      } else if (contextDataView.fields[ key ]) {
        contextField = contextDataView.fields[ key ];
      } else if (contextDataView.fields[ contextFieldId ]) {
        contextField = contextDataView.fields[ contextFieldId ];
      }

      if (contextField !== undefined) {
        const specField = formSpec.busFormSpec.fields[ key ];

        if (specField !== undefined) {
          isBlank = this.lmFieldUtilService.isBlank(specField.type, dvField.value);
        } else {
          isBlank = (dvField.value === undefined
            || dvField.value === null
            || trim(dvField.value as string) === '');
        }

        if (override || isBlank) {
          const field = Object.assign({}, dvField);
          field.value = contextField.value;
          dvFields[ key ] = field;
        }
      }
    }
  }

  private isValidSection(paramView: LMDataView, contextDataView: LMDataView, formSpec: LMFormSpec) {
    const busFormLayout = formSpec.busFormSpec.busFormLayoutSpec.layout;
    busFormLayout.sections.forEach((layoutSection: LMFormLayoutSection) => {
      const valid = layoutSection && layoutSection.visibility ? this.lmConditionUtilService.isValid(layoutSection.visibility, paramView) : true;
      if (layoutSection.columns) {
        layoutSection.columns.forEach((column: LMFormLayoutColumn) => {
          this.isValidLayoutElement(column.elements, paramView, contextDataView, formSpec, valid);
        });
      } else if (layoutSection.paragraph) {
        this.isValidLayoutElement(layoutSection.paragraph.elements, paramView, contextDataView, formSpec, valid);
      } else if (layoutSection.field) {
        this.markInvalidFields(layoutSection, paramView, contextDataView, formSpec, valid);
      }
    });
  }

  private isValidLayoutElement(elements: LMFormLayoutElement[], paramView: LMDataView, contextDataView: LMDataView, formSpec: LMFormSpec, valid: boolean) {
    elements.forEach((element: LMFormLayoutElement) => {
      if (element.paragraph) {
        this.isValidLayoutElement(element.paragraph.elements, paramView, contextDataView, formSpec, valid);
      } else if (element.field) {
        this.markInvalidFields(element, paramView, contextDataView, formSpec, valid);
      }
    });
  }

  private markInvalidFields(element: LMFormLayoutElement, paramView: LMDataView, contextDataView: LMDataView, formSpec: LMFormSpec, valid) {
    const elementValid = element.field && element.field.visibility ? this.lmConditionUtilService.isValid(element.field.visibility, paramView) : true;
    const specField = formSpec.busFormSpec.fields[ element.field.bindId ];
    const field = paramView.fields[element.field.bindId];

    // if there is a valid occurrence, invalid should be false. undefined value is equal to true
    if ((!valid || !elementValid) && field) {
      specField.inValid  = specField.inValid === undefined ? true : specField.inValid;
    } else {
      specField.inValid = false;
    }
  }

  private allParamsSet(fields: LMFields, specFields: LMSpecFields, actionFieldMap?: LMActionFieldMap): boolean {
    const keys: Array<string> = Object.keys(fields);
    let allParamsSet = true;

    if (actionFieldMap) {
      return true;
    }

    for (let index = 0, len = keys.length; index < len; index++) {
      const key = keys[ index ];
      const dvField = fields [ key ];
      const specField = specFields[ key ];

      let validField = true;
      if (specField === undefined) {
        validField = false;
      } else {
        validField = specField !== undefined && (specField.inValid === undefined || (specField.inValid !== undefined && !specField.inValid));
      }

      if (validField && dvField.mutable && this.lmFieldUtilService.isBlank(specField.type, dvField.value)) {
        allParamsSet = false;
      }
    }

    return allParamsSet;
  }

  public runCheckResponseRequiredPF(
    transactionState: LMTransactionState,
    pfAction: LMProcessFlowAction,
    busClassSpec: LMBusClassSpec,
    navigationContextForm?: LMNavContextKeys,
    busFormSpec?: LMBusFormSpec
  ): Observable<boolean> {
    return this.checkResponseRequiredPF(transactionState, 0, pfAction, busClassSpec, navigationContextForm, busFormSpec);
  }

  private checkResponseRequiredPF(
    transactionState: LMTransactionState,
    dataViewIndex: number,
    pfAction: LMProcessFlowAction,
    busClassSpec: LMBusClassSpec,
    navigationContextForm?: LMNavContextKeys,
    busFormSpec?: LMBusFormSpec
  ): Observable<boolean> {
    const requestList = transactionState.transactionRequestList;
    const isLastRequest = (dataViewIndex === requestList.length - 1);
    const actionRequest = requestList[ dataViewIndex ];
    const dataView = actionRequest.dataView;

    if (!this.lmResponseRequiredService.isResponseRequiredPF(pfAction)) {
      return of(true);
    }

    // call the service to ensure that dataView complies to all the required responses
    const okToAll = !isLastRequest && this.turnOnOKToAllResponseRequired;

    // Navigation context is used in reason code select
    // Form has the navigation context in the formSpec.
    // Whereas, if one or more workunits in a list are approved, get the navigation context for each record.
    let navigationContext: LMNavContextKeys = {};
    if (navigationContextForm) {
      navigationContext = navigationContextForm;
    } else if (dataView) {
      if (dataView.fields['PfiWorkunit']) {
        navigationContext['nc.PfiWorkunit'] = dataView.fields['PfiWorkunit'].value.toString();
      }
      if (dataView.fields['PfiActivity']) {
        navigationContext['nc.PfiActivity'] = dataView.fields['PfiActivity'].value.toString();
      }
      if (dataView.fields['PfiTask_prd_TaskName']) {
        navigationContext['nc.PfiTask.TaskName'] = dataView.fields['PfiTask_prd_TaskName'].value.toString();
      }
      if (dataView.fields['PfiTask_prd_TaskType']) {
        navigationContext['nc.PfiTask.TaskType'] = dataView.fields['PfiTask_prd_TaskType'].value.toString();
      }
      if (dataView.fields['PfiQueueAssignment']) {
        navigationContext['nc.PfiQueueAssignment'] = dataView.fields['PfiQueueAssignment'].value.toString();
      }
    }

    return this.lmResponseRequiredService.promptResponseRequiredDialogPF({
      pfAction,
      navigationContext,
      dataView,
      busFormSpec,
      busClassSpec,
      okToAll
    }).pipe(
      flatMap((response: ResponseRequiredResult) => {
        this.lmLoggerService.log(response.result);

        if (response.result === ResponseRequiredDialogResult.RESPONSE_CANCEL) {
          transactionState.cancel();
          return of(false);
        }

        const responseRequiredStatus = (!this.turnOnOKToAllResponseRequired) ?
          ResponseRequiredDialogResult.RESPONSE_OKTOALL :
          response.result;

        // clone of the target dataView + all the Response Required fields
        const respReqDataView = response.model.getData();
        this.applyRequiredFieldsPF(requestList[ dataViewIndex ].dataView, response.specFields, respReqDataView, pfAction);

        if (isLastRequest) {
          // continue the transaction the user has all required responses entered
          return of(true);
        }

        if (responseRequiredStatus === ResponseRequiredDialogResult.RESPONSE_OKTOALL) {
          ++dataViewIndex;
          for (let i = dataViewIndex; i < requestList.length; i++) {
            this.applyRequiredFieldsPF(requestList[ i ].dataView, response.specFields, respReqDataView, pfAction);
          }
          return of(true);
        }

        return this.checkResponseRequiredPF(transactionState, ++dataViewIndex, pfAction, busClassSpec, navigationContextForm, busFormSpec);
      })
    );
  }

  public runCheckResponseRequired(
    transactionState: LMTransactionState,
    busClassSpec: LMBusClassSpec,
    busFormSpec?: LMBusFormSpec
  ): Observable<boolean> {
    return this.checkResponseRequired(transactionState, 0, busClassSpec, busFormSpec);
  }

  private checkResponseRequired(
    transactionState: LMTransactionState,
    dataViewIndex: number,
    busClassSpec: LMBusClassSpec,
    busFormSpec?: LMBusFormSpec
  ): Observable<boolean> {
    const requestList = transactionState.transactionRequestList;
    const isLastRequest = (dataViewIndex === requestList.length - 1);
    const actionRequest = requestList[ dataViewIndex ];
    const dataView = actionRequest.dataView;
    const actionSpec = actionRequest.actionSpec;

    if (!this.lmResponseRequiredService.isResponseRequired(actionSpec, busFormSpec, dataView)) {
      return of(true);
    }

    // ensure that dataView complies to all the required responses
    const okToAll = !isLastRequest && this.turnOnOKToAllResponseRequired;
    return this.lmResponseRequiredService.promptResponseRequiredDialog({
      actionSpec,
      dataView,
      busFormSpec,
      busClassSpec,
      okToAll
    }).pipe(
      flatMap((response: ResponseRequiredResult) => {
        this.lmLoggerService.log(response.result);

        if (response.result === ResponseRequiredDialogResult.RESPONSE_CANCEL) {
          transactionState.cancel();
          return of(false);
        }

        const responseRequiredStatus = (!this.turnOnOKToAllResponseRequired) ?
          ResponseRequiredDialogResult.RESPONSE_OKTOALL :
          response.result;

        // clone of the target dataView + all the Response Required fields
        const respReqDataView = response.model.getData();
        this.applyRequiredFields(requestList[ dataViewIndex ].dataView, response.specFields, respReqDataView);

        if (isLastRequest) {
          // continue the transaction if the user has all required responses entered
          return of(true);
        }

        if (responseRequiredStatus === ResponseRequiredDialogResult.RESPONSE_OKTOALL) {
          ++dataViewIndex;
          for (let i = dataViewIndex; i < requestList.length; i++) {
            this.applyRequiredFields(requestList[ i ].dataView, response.specFields, respReqDataView);
          }
          return of(true);
        }

        return this.checkResponseRequired(transactionState, ++dataViewIndex, busClassSpec, busFormSpec);
      })
    );
  }

  private applyRequiredFields(dataView: LMDataView, specFields: LMSpecFields, respReqDataView: LMDataView): LMDataView {
    const keys = Object.keys(specFields);
    for (let i = 0, len = keys.length; i < len; i++) {
      const fieldId: string = keys[ i ];
      const respReqField: LMField = respReqDataView.fields[ fieldId ];
      const field: LMField = dataView.fields[ fieldId ];

      if (field) {
        field.value = respReqField.value;
      } else {
        dataView.fields[ fieldId ] = respReqField;
      }
    }
    return dataView;
  }

  private applyRequiredFieldsPF(dataView: LMDataView, specFields: LMSpecFields, respReqDataView: LMDataView, pfAction: LMProcessFlowAction): LMDataView {
    const processFlowAction = dataView.processFlowActions.find(pfActionLocal => pfActionLocal.name === pfAction.name);

    const keys: Array<string> = Object.keys(specFields);
    for (let i = 0, len = keys.length; i < len; i++) {
      const fieldId: string = keys[ i ];
      const respReqField: LMField = respReqDataView.fields[ fieldId ];
      if (fieldId.indexOf('_reason_code_pf') > -1) {
        processFlowAction.actionReason = respReqField.value.toString();
      }
      if (fieldId.indexOf('_action_comment_pf') > -1) {
        processFlowAction.reasonComment = respReqField.value.toString();
      }
    }
    return dataView;
  }

  public executeAction(transactionState: LMTransactionState): Observable<LMTransactionState> {
    return this.lmActionService.makeExecute({
      actionRequestArray: transactionState.transactionRequestList,
      busFormSpec: transactionState.busFormSpec,
      busListSpec: transactionState.busListSpec,
      parameterView: transactionState.parameterView,
      multiPartFields: transactionState.multiPartFields,
      templateMultiPartFields: transactionState.templateMultiPartFields,
      url: transactionState.url,
      isMatrixForm: transactionState.isMatrixForm
    }).pipe(
      flatMap((response) => {
        // got a single Transaction Result which indicates an exception occurred
        // before the engine even tried to run the transactions
        if (response.status) {
          throw response;
        }

        if (response.mfaUrl) {
          transactionState.finish();
          window.location.href = response.mfaUrl;
          return;
        }
        transactionState.response = response;
        return this.handleResponse(transactionState);
      }),
      flatMap((handledTransactionState: LMTransactionState) => {
        if (handledTransactionState.hasPendingTransactions) {
          handledTransactionState.resetTransactionRequest();
          return this.executeAction(handledTransactionState);
        }

        handledTransactionState.finish();
        return of(handledTransactionState);
      }),
      catchError((error) => {
        transactionState.error(error);
        return of(transactionState);
      })
    );
  }

  private handleResponse(transactionState: LMTransactionState): Observable<LMTransactionState> {
    const response = transactionState.response;

    if (response.postReg && response.postRegUrl) {
      // TODO this commented code need to go into forms and list
      // params.formModel.markAsPristine();
      // let postRegUrl: string = this.lmUrlUtilService.toRelativeUrl(result.postRegUrl);
      // window.location.href = postRegUrl

      transactionState.postRegUrl = response.postRegUrl;
      transactionState.finish();
      return of(transactionState);
    } else if (response.isGuestAction && response.redirectUrl) {
      transactionState.redirectUrlToGuestReg = response.redirectUrl;
      return of(transactionState);
    } else {
      return this.processTransactionResult(transactionState, response);
    }
  }

  private processTransactionResult(transactionState: LMTransactionState, resultList: LMActionResult[]): Observable<LMTransactionState> {

    if (transactionState.dataViewIndex < resultList.length) {
      const result: LMActionResult = resultList[ transactionState.dataViewIndex ];
      const request: LMActionRequest = transactionState.transactionRequestList[ transactionState.dataViewIndex ];

      if (this.isRuntimeConfirmationRequired(transactionState, result, request)) {
        return this.handleRuntimeConfirmation(transactionState, resultList, result, request);
      }

      // if the status is confirmation required, then the use must have already confirmed the confirmation
      // therefore we need to keey the transaction pending
      if (AppConstants.CONFIRMATION_REQUIRED !== result.status) {
        transactionState.completeResult(result);
      }
      transactionState.dataViewIndex++;
      return this.processTransactionResult(transactionState, resultList);
    }

    return of(transactionState);
  }

  private isRuntimeConfirmationRequired(transactionState: LMTransactionState, result: LMActionResult, request: LMActionRequest): boolean {
    if (AppConstants.UNMODIFIED_CONFIRMATION_REQUIRED === result.status) {
      return true;
    }

    if (AppConstants.CONFIRMATION_REQUIRED === result.status) {
      if (transactionState.hasCancelledRTConfirmationMessage(result.messageId)) {
        transactionState.completeResult(result);
        return false;
      }
      return request.confirmedMessageIds ? request.confirmedMessageIds.indexOf(result.messageId) === -1 : true;
    }

    return false;
  }

  // This is where confirmation exceptions are handled
  private handleRuntimeConfirmation(
    transactionState: LMTransactionState,
    resultList: LMActionResult[],
    result: LMActionResult,
    request: LMActionRequest
  ): Observable<LMTransactionState> {
    const unmodified = AppConstants.UNMODIFIED_CONFIRMATION_REQUIRED === result.status;
    const okToAll = !unmodified && (transactionState.dataViewIndex < resultList.length - 1);
    const message = result.message;

    return this.lmConfirmationMessageService.promptConfirmationMessage({ message, okToAll }).pipe(
      flatMap((confirmation: ConfirmationDialogResult) => {
        switch (confirmation) {
          case ConfirmationDialogResult.CONFIRMATION_OK:
            request = this.updateConfirmationMessage(result, request);
            transactionState.queueRequest(request);
            break;

          case ConfirmationDialogResult.CONFIRMATION_OKTOALL:
            transactionState = this.updateAllConfirmationMessages(transactionState, resultList);
            break;

          case ConfirmationDialogResult.CONFIRMATION_CANCEL:
            transactionState.addCancelledRTConfirmation(result);
            break;
        }
        // process the next result
        transactionState.dataViewIndex++;
        return this.processTransactionResult(transactionState, resultList);
      })
    );
  }

  private updateConfirmationMessage(result: LMActionResult, request: LMActionRequest): LMActionRequest {
    if (AppConstants.UNMODIFIED_CONFIRMATION_REQUIRED === result.status) {
      request.unmodifiedConfirmation = true;
    } else {
      request.confirmedMessageIds = request.confirmedMessageIds || [];
      request.confirmedMessageIds.push(result.messageId);
    }

    return request;
  }

  private updateAllConfirmationMessages(transactionState: LMTransactionState, resultList: LMActionResult[]): LMTransactionState {
    transactionState.transactionRequestList.forEach((request: LMActionRequest, index: number) => {
      const result = resultList[index];
      if (AppConstants.CONFIRMATION_REQUIRED !== result.status) {
        transactionState.completeResult(result);
      } else {
        transactionState.queueRequest(this.updateConfirmationMessage(result, request));
      }
    });
    return transactionState;
  }

  public processStepUpAuthActionResult(transactionState: LMTransactionState, result: LMActionResult, request: LMActionRequest): Observable<LMTransactionState> {
    if (result.status === AppConstants.CONFIRMATION_REQUIRED) {
      if (this.isRuntimeConfirmationRequired(transactionState, result, request)) {
        return this.handleRuntimeConfirmation(transactionState, [ result ], result, request).pipe(
          flatMap((handledTransactionState: LMTransactionState) => {
            if (handledTransactionState.hasPendingTransactions) {
              handledTransactionState.resetTransactionRequest();
              return this.executeAction(handledTransactionState);
            }

            handledTransactionState.finish();
            return of(handledTransactionState);
          }),
          catchError((error) => {
            transactionState.error(error);
            return of(transactionState);
          })
        );
      }
    } else {
      return of(transactionState);
    }
  }
}
